import React, { useState } from 'react'
import {
  useClick,
  useFloating,
  useInteractions,
  Placement,
  offset,
  flip,
  shift,
  autoUpdate,
  useDismiss,
  size,
} from '@floating-ui/react-dom-interactions'

import { MenuButton } from 'components/styled/menu/menu-item'
import styled, { css } from 'styled-components'
import { BaseMenuButtonProps } from 'components/styled/menu/menu-item/base-menu-button'
import { Menu, MenuWrapper } from 'components/styled/menu'
import { StyledMenuButton } from 'components/styled/menu/menu-item/menu-button'
import { POPOVER_ID } from 'components/styled/popover/popover-root'
import { ThemeSpace } from 'theme/spaces'

export interface PopoverMenuProps {
  triggerButtonProps: BaseMenuButtonProps
  rootId?: string
  placement?: Placement
  defaultIsOpen?: boolean
  isScrollable?: boolean
  maxWidth?: ThemeSpace
  maxTriggerWidth?: ThemeSpace
  fullWidth?: boolean
  children?: React.ReactNode
  customIsMenuOpen?: boolean
  customSetMenuOpen?: (isOpen: boolean) => void
}

const RoundedStyle = css`
  ${StyledMenuButton} {
    border-radius: ${({ theme }) => theme.space.s20};
    padding: ${({ theme }) => theme.space.s8} ${({ theme }) => theme.space.s16};
  }
`

export const StyledPopoverMenuButton = styled(MenuButton)<{
  $fullWidth: boolean
  $maxTriggerWidth?: ThemeSpace
  $buttonStyle?: 'default' | 'rounded'
}>`
  width: ${({ $fullWidth, $maxTriggerWidth, theme }) =>
    $fullWidth
      ? '100% '
      : $maxTriggerWidth
      ? theme.space[$maxTriggerWidth]
      : 'fit-content'};

  ${({ $buttonStyle }) => $buttonStyle === 'rounded' && RoundedStyle}
`

const StyledMenuWrapper = styled.div`
  z-index: ${({ theme }) => theme.zIndex.popover};
  display: flex;
`

export const StyledMenuContent = styled.div<{
  $isScrollable: boolean
  $maxWidth: ThemeSpace
  $fullWidth: boolean
}>`
  position: relative;

  background-color: ${({ theme }) => theme.color.white};
  box-shadow: ${({ theme }) => theme.shadow.default};
  border: 1px solid ${({ theme }) => theme.color.grey10};
  border-radius: ${({ theme }) => theme.space.s4};

  padding: ${({ theme }) => theme.space.s16};

  display: flex;
  flex-direction: column;
  overflow: ${({ $isScrollable }) => ($isScrollable ? 'auto' : 'initial')};

  flex-grow: 0;
  width: ${({ theme, $fullWidth }) =>
    $fullWidth ? `calc(100vw - ${theme.space.s32})` : 'fit-content'};
  min-width: ${({ theme }) => theme.space.s160};

  max-width: ${({ theme, $maxWidth, $fullWidth }) =>
    $fullWidth ? '100vw' : theme.space[$maxWidth]};

  ${MenuWrapper} {
    width: ${({ $fullWidth }) => ($fullWidth ? '100%' : 'fit-content')};
  }
`

export const PopoverMenu: React.FC<PopoverMenuProps> = ({
  triggerButtonProps,
  placement = 'bottom-end',
  rootId = POPOVER_ID,
  defaultIsOpen = false,
  isScrollable = false,
  maxWidth = 's400',
  maxTriggerWidth,
  fullWidth = false,
  children,
  customSetMenuOpen,
  customIsMenuOpen,
  ...props
}) => {
  const [isMenuOpen, setMenuOpen] = useState(defaultIsOpen)
  const { x, y, reference, floating, strategy, context } = useFloating({
    open: customIsMenuOpen || isMenuOpen,
    onOpenChange: (open) => {
      if (customSetMenuOpen) {
        customSetMenuOpen(open)
      } else {
        setMenuOpen(open)
      }
    },
    placement,
    middleware: [
      offset(8),
      flip(), // flips the menu to the top/bottom of the button if default location (placement) has not enough space
      shift(), // shifts the menu to the left/right of the button if there's not enough space
      ...(isScrollable
        ? [
            size({
              apply({ availableHeight, elements }) {
                const maxHeight = availableHeight > 512 ? 512 : availableHeight
                Object.assign(elements.floating.style, {
                  maxHeight: `${maxHeight}px`,
                })
              },
            }),
          ]
        : []),
    ],
    whileElementsMounted: (reference, floating, update) =>
      autoUpdate(reference, floating, update, { layoutShift: false }),
  })
  const { getReferenceProps, getFloatingProps } = useInteractions([
    useClick(context), // opens the menu when button is clicked
    useDismiss(context), // closes the menu when user clicks outside the menu
  ])

  return (
    <>
      <StyledPopoverMenuButton
        {...triggerButtonProps}
        iconRight={
          customIsMenuOpen || isMenuOpen
            ? triggerButtonProps.iconRightClose || 'expand_less'
            : triggerButtonProps.iconRight || 'expand_more'
        }
        ref={reference}
        isSelected={customIsMenuOpen || isMenuOpen}
        $fullWidth={fullWidth}
        $maxTriggerWidth={maxTriggerWidth}
        $buttonStyle={triggerButtonProps.buttonStyle}
        {...getReferenceProps()}
        {...props}
      />
      {(customIsMenuOpen || isMenuOpen) && (
        <StyledMenuWrapper
          ref={floating}
          style={{
            position: strategy,
            top: y ?? 0,
            left: x ?? 0,
          }}
          {...getFloatingProps()}
        >
          <StyledMenuContent
            $fullWidth={fullWidth}
            $isScrollable={isScrollable}
            $maxWidth={maxWidth}
          >
            <Menu onClose={() => setMenuOpen(false)}>{children}</Menu>
          </StyledMenuContent>
        </StyledMenuWrapper>
      )}
    </>
  )
}
